/**
 * CMS React Hooks - React Query hooks for CMS content
 * Phase CMS-1: Foundation
 *
 * Purpose:
 * Provides React hooks for accessing CMS content with React Query caching.
 * Uses long staleTime (60 minutes) since CMS content changes infrequently.
 *
 * Usage:
 * ```typescript
 * import { useTierContent, useTierPricing, useUpsellMessage } from '@/lib/cms/hooks';
 *
 * // Get tier content
 * const { data: tier, isLoading } = useTierContent('drama_plus');
 *
 * // Get pricing options
 * const { data: pricing } = useTierPricing('drama_plus', 'individual');
 *
 * // Get contextual upsell message
 * const { data: upsell } = useUpsellMessage('free', 'quota_exhausted');
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import {
  cmsClient,
  type TierId,
  type PlanType,
  type UpsellContext,
  type QuipCategory,
  type TierContent,
  type PricingProduct,
  type UpsellMessage,
  type Quip,
  type Experiment,
  type CMSFeature,
} from '@/lib/cms/client';

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query keys for CMS data - follows React Query best practices
 * for hierarchical cache invalidation.
 *
 * Examples:
 * - Invalidate all CMS: queryClient.invalidateQueries({ queryKey: cmsQueryKeys.all })
 * - Invalidate tiers: queryClient.invalidateQueries({ queryKey: cmsQueryKeys.tiers.all })
 * - Invalidate specific tier: queryClient.invalidateQueries({ queryKey: cmsQueryKeys.tiers.detail('drama_plus') })
 */
export const cmsQueryKeys = {
  /** All CMS queries */
  all: ['cms'] as const,

  /** Tier content queries */
  tiers: {
    all: ['cms', 'tiers'] as const,
    list: () => [...cmsQueryKeys.tiers.all, 'list'] as const,
    detail: (tierId: TierId) => [...cmsQueryKeys.tiers.all, 'detail', tierId] as const,
  },

  /** Pricing queries */
  pricing: {
    all: ['cms', 'pricing'] as const,
    byTier: (tierId: TierId, planType: PlanType) =>
      [...cmsQueryKeys.pricing.all, tierId, planType] as const,
  },

  /** Upsell message queries */
  upsell: {
    all: ['cms', 'upsell'] as const,
    byContext: (currentTier: TierId, context: UpsellContext) =>
      [...cmsQueryKeys.upsell.all, currentTier, context] as const,
  },

  /** Quip queries */
  quips: {
    all: ['cms', 'quips'] as const,
    random: (category: QuipCategory, tierId?: TierId) =>
      [...cmsQueryKeys.quips.all, 'random', category, tierId] as const,
  },

  /** Experiment queries */
  experiments: {
    all: ['cms', 'experiments'] as const,
    byKey: (experimentKey: string) =>
      [...cmsQueryKeys.experiments.all, experimentKey] as const,
  },

  /** Feature queries */
  features: {
    all: ['cms', 'features'] as const,
    list: () => [...cmsQueryKeys.features.all, 'list'] as const,
    availability: (featureKey: string, tierId: TierId) =>
      [...cmsQueryKeys.features.all, 'availability', featureKey, tierId] as const,
  },
} as const;

// ============================================================================
// Default Cache Settings
// ============================================================================

/**
 * CMS content rarely changes - use long cache times
 * to minimize API calls and improve performance.
 */
const CMS_STALE_TIME = 60 * 60 * 1000; // 60 minutes
const CMS_GC_TIME = 2 * 60 * 60 * 1000; // 2 hours (garbage collection)

// ============================================================================
// Tier Content Hooks
// ============================================================================

/**
 * Options for the useTierContent hook.
 * Extends React Query options, excluding queryKey and queryFn which are
 * managed internally.
 */
export interface UseTierContentOptions
  extends Omit<
    UseQueryOptions<TierContent, Error, TierContent>,
    'queryKey' | 'queryFn'
  > {}

/**
 * Hook for fetching tier marketing content.
 * Falls back to constants.ts when CMS is disabled.
 *
 * @param tierId - Tier ID to fetch content for
 * @param options - Optional React Query options
 *
 * @example
 * ```tsx
 * function TierCard({ tierId }: { tierId: TierId }) {
 *   const { data: tier, isLoading } = useTierContent(tierId);
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   return (
 *     <Card>
 *       <h2>{tier?.display_name}</h2>
 *       <p>{tier?.tagline}</p>
 *       {tier?.badge && <Badge>{tier.badge}</Badge>}
 *     </Card>
 *   );
 * }
 * ```
 */
export function useTierContent(
  tierId: TierId,
  options?: UseTierContentOptions
): UseQueryResult<TierContent, Error> {
  return useQuery({
    queryKey: cmsQueryKeys.tiers.detail(tierId),
    queryFn: () => cmsClient.getTierContent(tierId),
    staleTime: CMS_STALE_TIME,
    gcTime: CMS_GC_TIME,
    ...options,
  });
}

/**
 * Options for the useAllTiers hook.
 * Extends React Query options, excluding queryKey and queryFn which are
 * managed internally.
 */
export interface UseAllTiersOptions
  extends Omit<
    UseQueryOptions<TierContent[], Error, TierContent[]>,
    'queryKey' | 'queryFn'
  > {}

/**
 * Hook for fetching all visible tiers for pricing page.
 *
 * @param options - Optional React Query options
 *
 * @example
 * ```tsx
 * function PricingPage() {
 *   const { data: tiers, isLoading } = useAllTiers();
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   return (
 *     <div className="grid grid-cols-3 gap-4">
 *       {tiers?.map((tier) => (
 *         <TierCard key={tier.tier_id} tier={tier} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAllTiers(
  options?: UseAllTiersOptions
): UseQueryResult<TierContent[], Error> {
  return useQuery({
    queryKey: cmsQueryKeys.tiers.list(),
    queryFn: () => cmsClient.getAllTiers(),
    staleTime: CMS_STALE_TIME,
    gcTime: CMS_GC_TIME,
    ...options,
  });
}

// ============================================================================
// Pricing Hooks
// ============================================================================

/**
 * Options for the useTierPricing hook.
 * Extends React Query options, excluding queryKey and queryFn which are
 * managed internally.
 */
export interface UseTierPricingOptions
  extends Omit<
    UseQueryOptions<PricingProduct[], Error, PricingProduct[]>,
    'queryKey' | 'queryFn'
  > {}

/**
 * Hook for fetching tier pricing options.
 * Returns pricing for all billing intervals (weekly, monthly, quarterly, etc.)
 *
 * @param tierId - Tier to fetch pricing for
 * @param planType - 'individual' or 'family'
 * @param options - Optional React Query options
 *
 * @example
 * ```tsx
 * function PricingSelector({ tierId }: { tierId: TierId }) {
 *   const [planType, setPlanType] = useState<PlanType>('individual');
 *   const { data: pricing, isLoading } = useTierPricing(tierId, planType);
 *
 *   return (
 *     <div>
 *       <ToggleGroup value={planType} onValueChange={setPlanType}>
 *         <ToggleGroupItem value="individual">Individual</ToggleGroupItem>
 *         <ToggleGroupItem value="family">Family</ToggleGroupItem>
 *       </ToggleGroup>
 *
 *       {pricing?.map((product) => (
 *         <PricingCard key={product.product_key} product={product} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useTierPricing(
  tierId: TierId,
  planType: PlanType = 'individual',
  options?: UseTierPricingOptions
): UseQueryResult<PricingProduct[], Error> {
  return useQuery({
    queryKey: cmsQueryKeys.pricing.byTier(tierId, planType),
    queryFn: () => cmsClient.getTierPricing(tierId, planType),
    staleTime: CMS_STALE_TIME,
    gcTime: CMS_GC_TIME,
    ...options,
  });
}

// ============================================================================
// Upsell Message Hooks
// ============================================================================

/**
 * Options for the useUpsellMessage hook.
 * Extends React Query options, excluding queryKey and queryFn which are
 * managed internally.
 */
export interface UseUpsellMessageOptions
  extends Omit<
    UseQueryOptions<UpsellMessage | null, Error, UpsellMessage | null>,
    'queryKey' | 'queryFn'
  > {}

/**
 * Hook for fetching contextual upsell messages.
 * Returns null when CMS is disabled (CMS-only feature).
 *
 * @param currentTier - User's current tier
 * @param context - Upsell context (quota_exhausted, feature_gate, etc.)
 * @param options - Optional React Query options
 *
 * @example
 * ```tsx
 * function QuotaExhaustedModal({ currentTier }: { currentTier: TierId }) {
 *   const { data: upsell } = useUpsellMessage(currentTier, 'quota_exhausted');
 *
 *   if (!upsell) {
 *     // Fallback to hardcoded content when CMS disabled
 *     return <DefaultUpgradePrompt />;
 *   }
 *
 *   return (
 *     <Modal>
 *       <h2>{upsell.headline}</h2>
 *       <p>{upsell.body}</p>
 *       <Button>{upsell.cta_text}</Button>
 *       {upsell.cta_secondary_text && (
 *         <Button variant="ghost">{upsell.cta_secondary_text}</Button>
 *       )}
 *     </Modal>
 *   );
 * }
 * ```
 */
export function useUpsellMessage(
  currentTier: TierId,
  context: UpsellContext,
  options?: UseUpsellMessageOptions
): UseQueryResult<UpsellMessage | null, Error> {
  return useQuery({
    queryKey: cmsQueryKeys.upsell.byContext(currentTier, context),
    queryFn: () => cmsClient.getUpsellMessage(currentTier, context),
    staleTime: CMS_STALE_TIME,
    gcTime: CMS_GC_TIME,
    ...options,
  });
}

// ============================================================================
// Quip Hooks
// ============================================================================

/**
 * Options for the useRandomQuip hook.
 * Extends React Query options, excluding queryKey and queryFn which are
 * managed internally.
 */
export interface UseRandomQuipOptions
  extends Omit<
    UseQueryOptions<Quip | null, Error, Quip | null>,
    'queryKey' | 'queryFn'
  > {}

/**
 * Hook for fetching a random quip by category.
 * Returns null when CMS is disabled (CMS-only feature).
 *
 * Note: This hook returns a cached quip. For truly random quips,
 * invalidate the query or use refetch().
 *
 * @param category - Quip category (loading, error, success, etc.)
 * @param tierId - Optional tier to filter quips
 * @param options - Optional React Query options
 *
 * @example
 * ```tsx
 * function LoadingState() {
 *   const { data: quip, refetch } = useRandomQuip('loading');
 *
 *   return (
 *     <div className="flex flex-col items-center gap-4">
 *       <Spinner />
 *       {quip && (
 *         <Text className="text-sm text-gray-500 italic">
 *           "{quip.content}"
 *           {quip.author && <span> — {quip.author}</span>}
 *         </Text>
 *       )}
 *       <Button variant="ghost" size="sm" onPress={() => refetch()}>
 *         🎲 New Quip
 *       </Button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useRandomQuip(
  category: QuipCategory,
  tierId?: TierId,
  options?: UseRandomQuipOptions
): UseQueryResult<Quip | null, Error> {
  return useQuery({
    queryKey: cmsQueryKeys.quips.random(category, tierId),
    queryFn: () => cmsClient.getRandomQuip(category, tierId),
    // Shorter stale time for quips - they're meant to be fresh
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: CMS_GC_TIME,
    ...options,
  });
}

// ============================================================================
// Experiment Hooks
// ============================================================================

/**
 * Options for the useExperiment hook.
 * Extends React Query options, excluding queryKey and queryFn which are
 * managed internally.
 */
export interface UseExperimentOptions
  extends Omit<
    UseQueryOptions<Experiment | null, Error, Experiment | null>,
    'queryKey' | 'queryFn'
  > {}

/**
 * Hook for fetching experiment configuration.
 * Returns null when CMS is disabled (CMS-only feature).
 *
 * @param experimentKey - Experiment key to fetch
 * @param options - Optional React Query options
 *
 * @example
 * ```tsx
 * function FeatureWithExperiment() {
 *   const { data: experiment } = useExperiment('pricing_page_layout');
 *
 *   // Determine variant based on experiment config
 *   const variant = useMemo(() => {
 *     if (!experiment || experiment.status !== 'running') {
 *       return 'control';
 *     }
 *     // Use PostHog feature flag for actual assignment
 *     return getVariantFromPostHog(experiment.experiment_key);
 *   }, [experiment]);
 *
 *   if (variant === 'grid') {
 *     return <GridLayout />;
 *   }
 *   return <DefaultLayout />;
 * }
 * ```
 */
export function useExperiment(
  experimentKey: string,
  options?: UseExperimentOptions
): UseQueryResult<Experiment | null, Error> {
  return useQuery({
    queryKey: cmsQueryKeys.experiments.byKey(experimentKey),
    queryFn: () => cmsClient.getExperiment(experimentKey),
    staleTime: CMS_STALE_TIME,
    gcTime: CMS_GC_TIME,
    ...options,
  });
}

// ============================================================================
// Feature Hooks
// ============================================================================

/**
 * Options for the useAllFeatures hook.
 * Extends React Query options, excluding queryKey and queryFn which are
 * managed internally.
 */
export interface UseAllFeaturesOptions
  extends Omit<
    UseQueryOptions<CMSFeature[], Error, CMSFeature[]>,
    'queryKey' | 'queryFn'
  > {}

/**
 * Hook for fetching all visible features with tier gating.
 * Returns empty array when CMS is disabled.
 *
 * @param options - Optional React Query options
 *
 * @example
 * ```tsx
 * function FeatureComparisonTable() {
 *   const { data: features, isLoading } = useAllFeatures();
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   return (
 *     <Table>
 *       <TableHeader>
 *         <TableRow>
 *           <TableHead>Feature</TableHead>
 *           <TableHead>Free</TableHead>
 *           <TableHead>Drama+</TableHead>
 *           <TableHead>DramaX</TableHead>
 *         </TableRow>
 *       </TableHeader>
 *       <TableBody>
 *         {features?.map((feature) => (
 *           <FeatureRow key={feature.feature_key} feature={feature} />
 *         ))}
 *       </TableBody>
 *     </Table>
 *   );
 * }
 * ```
 */
export function useAllFeatures(
  options?: UseAllFeaturesOptions
): UseQueryResult<CMSFeature[], Error> {
  return useQuery({
    queryKey: cmsQueryKeys.features.list(),
    queryFn: () => cmsClient.getAllFeatures(),
    staleTime: CMS_STALE_TIME,
    gcTime: CMS_GC_TIME,
    ...options,
  });
}

/**
 * Options for the useFeatureAvailability hook.
 * Extends React Query options, excluding queryKey and queryFn which are
 * managed internally.
 */
export interface UseFeatureAvailabilityOptions
  extends Omit<
    UseQueryOptions<boolean, Error, boolean>,
    'queryKey' | 'queryFn'
  > {}

/**
 * Hook for checking if a feature is available for a tier.
 * Falls back to tier-based logic when CMS is disabled.
 *
 * @param featureKey - Feature to check
 * @param currentTier - User's current tier
 * @param options - Optional React Query options
 *
 * @example
 * ```tsx
 * function PremiumFeature() {
 *   const { tier } = useAuth();
 *   const { data: hasAccess, isLoading } = useFeatureAvailability(
 *     'pattern_detection',
 *     tier
 *   );
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   if (!hasAccess) {
 *     return <UpgradePrompt feature="pattern_detection" />;
 *   }
 *
 *   return <PatternDetectionFeature />;
 * }
 * ```
 */
export function useFeatureAvailability(
  featureKey: string,
  currentTier: TierId,
  options?: UseFeatureAvailabilityOptions
): UseQueryResult<boolean, Error> {
  return useQuery({
    queryKey: cmsQueryKeys.features.availability(featureKey, currentTier),
    queryFn: () => cmsClient.isFeatureAvailable(featureKey, currentTier),
    staleTime: CMS_STALE_TIME,
    gcTime: CMS_GC_TIME,
    ...options,
  });
}

// ============================================================================
// Cache Invalidation Helpers
// ============================================================================

/**
 * Helper for invalidating CMS cache after content updates.
 * Useful for admin dashboard or CMS integration.
 *
 * @example
 * ```tsx
 * import { useQueryClient } from '@tanstack/react-query';
 * import { invalidateCMSCache } from '@/lib/cms/hooks';
 *
 * function AdminCMSEditor() {
 *   const queryClient = useQueryClient();
 *
 *   const handleSave = async (content) => {
 *     await saveToCMS(content);
 *     // Invalidate all CMS cache to pick up changes
 *     invalidateCMSCache(queryClient);
 *   };
 *
 *   return <CMSEditor onSave={handleSave} />;
 * }
 * ```
 */
export function invalidateCMSCache(
  queryClient: import('@tanstack/react-query').QueryClient,
  scope?: 'all' | 'tiers' | 'pricing' | 'upsell' | 'quips' | 'experiments' | 'features'
): void {
  switch (scope) {
    case 'tiers':
      queryClient.invalidateQueries({ queryKey: cmsQueryKeys.tiers.all });
      break;
    case 'pricing':
      queryClient.invalidateQueries({ queryKey: cmsQueryKeys.pricing.all });
      break;
    case 'upsell':
      queryClient.invalidateQueries({ queryKey: cmsQueryKeys.upsell.all });
      break;
    case 'quips':
      queryClient.invalidateQueries({ queryKey: cmsQueryKeys.quips.all });
      break;
    case 'experiments':
      queryClient.invalidateQueries({ queryKey: cmsQueryKeys.experiments.all });
      break;
    case 'features':
      queryClient.invalidateQueries({ queryKey: cmsQueryKeys.features.all });
      break;
    case 'all':
    default:
      queryClient.invalidateQueries({ queryKey: cmsQueryKeys.all });
  }
}

// Note: Default export intentionally removed.
// Named exports are preferred for React hooks as they:
// 1. Enable tree-shaking for unused hooks
// 2. Provide clearer imports: import { useTierContent } from '@/hooks/useCMS'
// 3. Match React Query hook conventions
